import { Observable } from 'rxjs';
import { EventEmitter, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { PatientService } from '../../global-services/patient.service';
import { CreatePatientModel, UpdatePatientModel, PatientModel, EthnicityType, SexType } from '../../global-data-models/patient.model';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { v4 as uuidv4 } from 'uuid';

import { Misc } from '../../global-misc/misc';
import { CountryModel } from '../../global-data-models/country.model';
import { Router } from '@angular/router';

export enum CrudPatientMode {
  CREATE,
  READ,
  UPDATE,
}
export const DEFAULT_CRUD_PATIENT_MODE = CrudPatientMode.CREATE;

export interface CrudPatientUiState {
  form: FormGroup;
  crudMode: CrudPatientMode;
  sexes: SexType[];
  ethnicities: EthnicityType[];
  hasConsent?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CrudPatientFacade {
  public uiState: CrudPatientUiState;

  private uiStateEvent: EventEmitter<CrudPatientUiState> = new EventEmitter<CrudPatientUiState>();
  public uiState$: Observable<CrudPatientUiState> = this.uiStateEvent.asObservable();

  private orgId?: string;

  constructor(
    private readonly loadingService: GlobalLoadingService,
    private readonly fb: FormBuilder,
    private readonly patientService: PatientService,
    private readonly messageService: GlobalMessageService,
    private readonly router: Router,
  ) {
    /* assign a temporary blank ui state */
    this.uiState = {
      form: this.createForm(undefined),
      crudMode: DEFAULT_CRUD_PATIENT_MODE,
      sexes: [],
      ethnicities: [],
    };

    this.uiStateEvent.emit(this.uiState);
  }

  public async begin(_orgId: string, _crudMode: CrudPatientMode, patientId?: string) {
    try {
      this.loadingService.show();

      this.orgId = _orgId;

      let patientData: CreatePatientModel | undefined = undefined;

      if (_crudMode === CrudPatientMode.READ || _crudMode === CrudPatientMode.UPDATE) {
        /* if read or update then patientId should be present and user data should be fetched */
        if (!patientId) {
          /* too annoying to toast this each time so just log. it happens each time our patientId is changed */
          console.log('Tried to read|update but patientId undefined.');
          return;
        }

        patientData = await this.patientService.getPatient(this.orgId!, patientId);
      }

      const _ethnicities: EthnicityType[] = await this.patientService.getEthnicities();
      const _sexes: SexType[] = await this.patientService.getSexes();

      const _form = this.createForm(patientData as PatientModel, _crudMode);

      if (_crudMode === CrudPatientMode.READ) {
        _form.disable();
        console.log('disable form as read mode');
      }

      const hasConsented = patientData?.consentData?.['consented'] ?? false;
     

      this.uiState = {
        form: _form,
        crudMode: _crudMode,
        sexes: _sexes,
        ethnicities: _ethnicities,
        hasConsent: hasConsented ?? false,
      };

      this.uiStateEvent.emit(this.uiState);
    } catch (error: any) {
      this.messageService.showError('Error', error.message);
      throw Error(error);
    } finally {
      this.loadingService.hide();
    }
  }

  public resetForm(): void {
    /* FIXME: this wont reset the ui state, need to set the default reset values */
    this.uiState.form.reset();
  }

  public setConsentConfirmed(confirmed: boolean): void {
    this.uiState.form.get('consentConfirmed')?.setValue(confirmed);
    this.uiState.form.get('consentConfirmed')?.markAsTouched();
    this.uiStateEvent.emit(this.uiState);
  }

  public async onConsentSubmitted(consentStatus: 'given' | 'not-given'): Promise<void> {
    try {
      this.loadingService.show();

      this.setConsentConfirmed(consentStatus === 'given');

      // Handle CREATE mode
      if (this.uiState.crudMode === CrudPatientMode.CREATE) {
        if (consentStatus === 'given') {
          const patientModel = this.formToPatientModel(consentStatus === 'given' ? true : false);
          await this.patientService.createPatient(this.orgId!, patientModel as CreatePatientModel);
          this.messageService.showSuccess('Success', 'Patient created successfully with consent');
        } else {
          this.messageService.showInfo('Info', 'Patient consent was not given. Patient not created.');
        }
      } else {
        // For UPDATE mode, just record the consent status without updating the patient
        this.messageService.showSuccess('Success', `Patient consent ${consentStatus === 'given' ? 'granted' : 'declined'} and recorded successfully`);
      }
    } catch (error: unknown) {
      const errorMessage: string = Misc.getErrorMessage(error);
      this.messageService.showError('Error', `Failed to ${this.uiState.crudMode === CrudPatientMode.CREATE ? 'create' : 'update'} patient: ` + errorMessage);
      throw new Error(error as any);
    } finally {
      this.loadingService.hide();
    }
  }

  public async submit(consentGiven: boolean) {
    const patientModel = this.formToPatientModel(consentGiven);

    try {
      if (this.uiState.crudMode === CrudPatientMode.CREATE) {
        const patient = await this.patientService.createPatient(this.orgId!, patientModel as CreatePatientModel);
        this.messageService.showSuccess('Success', 'Patient created successfully with consent');
        //send us through to the add visit page
        await this.router.navigate(['orgs', this.orgId, 'patients', patient.id, 'visit', 'create']);
        return;
      }

      if (this.uiState.crudMode === CrudPatientMode.UPDATE) {
        const patient = await this.patientService.updatePatient(this.orgId!, this.uiState.form.get('id')?.getRawValue(), patientModel as UpdatePatientModel);
        this.messageService.showSuccess('Success', 'Patient updated successfully');
      }
    } catch (error: unknown) {
      const errorMessage: string = Misc.getErrorMessage(error);
      this.messageService.showError('Error', 'Failed to submit patient: ' + errorMessage);
      throw new Error(error as any);
    }
  }
  private formToPatientModel(consentGiven: boolean): CreatePatientModel | UpdatePatientModel {
    const formValues = this.uiState.form.getRawValue();

    // Handle phone input value
    const code = formValues.telephone_code;
    let number = formValues.telephone || '';
    let normalized = number.trim();
    while (normalized.startsWith('0') && normalized.length > 1) {
      normalized = normalized.substring(1);
    }
    const telephoneValue = normalized ? `+${code}${normalized}` : undefined;

    const patientModel: Record<string, any> = {
      title: formValues.title,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email?.trim() || undefined,
      telephone: normalized,
      telephone_code: code,
      dateOfBirth: formValues.dateOfBirth,
      parentalMyopia: formValues.parentalMyopia,
      ethnicity_id: formValues.ethnicity_id,
      sex_id: formValues.sex_id,
      org_id: this.orgId,
      consentData: { consented: consentGiven, timestamp: new Date().toISOString() },
    };

    if (this.uiState.crudMode == CrudPatientMode.UPDATE) {
      patientModel['id'] = formValues.id;
    }

    return patientModel;
  }
  private createForm(patientData?: PatientModel, crudMode: CrudPatientMode = CrudPatientMode.CREATE): FormGroup {
    console.log('createForm patientData = ', patientData);

    // In CREATE mode, consent is not required for form validation since it will be handled in the popup
    // In UPDATE mode, consent validation depends on whether consent was previously given
    // In READ mode, no consent validation needed
    const isConsentRequired = crudMode === CrudPatientMode.UPDATE && !patientData?.consentData;

    return this.fb.group({
      id: [{ value: patientData?.id || uuidv4(), disabled: true }],
      org_id: [{ value: this.orgId!, disabled: true }],
      title: [patientData?.title || '', Validators.required],
      firstName: [patientData?.firstName || '', Validators.required],
      lastName: [patientData?.lastName || '', Validators.required],
      email: [patientData?.email?.trim() || undefined],
      telephone_code: [patientData?.telephone_code || ''],
      telephone: [patientData?.telephone || ''],
      dateOfBirth: [patientData?.dateOfBirth ? new Date(patientData.dateOfBirth) : '', Validators.required],
      parentalMyopia: [patientData?.parentalMyopia || '', Validators.required],
      ethnicity_id: [patientData?.ethnicity_id || '', Validators.required],
      sex_id: [patientData?.sex_id || '', Validators.required],
      consentConfirmed: [patientData?.consentData ? true : false, isConsentRequired ? Validators.requiredTrue : null],
    });
  }
}
